import React, { useState } from 'react';

import { formatRelativeTime } from '../../utils/dateUtils';
import { emailLogs } from '../../utils/stubData';

function Communications() {
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    template: '',
    dateRange: 'all',
  });

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const filteredLogs = emailLogs.filter(log => {
    const searchMatch =
      !filters.search ||
      log.recipientEmail.toLowerCase().includes(filters.search.toLowerCase()) ||
      log.subject.toLowerCase().includes(filters.search.toLowerCase());

    const statusMatch = !filters.status || log.status === filters.status;
    const templateMatch = !filters.template || log.templateName === filters.template;

    return searchMatch && statusMatch && templateMatch;
  });

  const getStatusIcon = status => {
    switch (status) {
      case 'delivered':
        return '✓';
      case 'bounced':
        return '✕';
      case 'pending':
        return '⏳';
      default:
        return '?';
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'delivered':
        return 'bg-green-600';
      case 'bounced':
        return 'bg-red-600';
      case 'pending':
        return 'bg-yellow-600';
      default:
        return 'bg-gray-600';
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-hdlcc-textDark uppercase">
            Communication History
          </h2>
          <p className="text-hdlcc-textMuted">Track all email communications</p>
        </div>
        <button className="bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 transition-colors">
          Send Bulk Email
        </button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Total Sent</h4>
          <p className="text-2xl font-bold mt-2 text-hdlcc-textDark">{emailLogs.length}</p>
          <p className="text-sm text-hdlcc-textMuted mt-1">All time</p>
        </div>
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Delivered</h4>
          <p className="text-2xl font-bold mt-2 text-hdlcc-accentGreen">
            {emailLogs.filter(l => l.status === 'delivered').length}
          </p>
          <p className="text-sm text-hdlcc-textMuted mt-1">
            {(
              (emailLogs.filter(l => l.status === 'delivered').length / emailLogs.length) *
              100
            ).toFixed(1)}
            %
          </p>
        </div>
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Opened</h4>
          <p className="text-2xl font-bold mt-2 text-blue-400">
            {emailLogs.filter(l => l.openedAt).length}
          </p>
          <p className="text-sm text-hdlcc-textMuted mt-1">
            {((emailLogs.filter(l => l.openedAt).length / emailLogs.length) * 100).toFixed(1)}% open
            rate
          </p>
        </div>
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Bounced</h4>
          <p className="text-2xl font-bold mt-2 text-hdlcc-accentRed">
            {emailLogs.filter(l => l.status === 'bounced').length}
          </p>
          <p className="text-sm text-hdlcc-textMuted mt-1">Requires attention</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="comms-search" className="block text-sm text-hdlcc-textMuted mb-1">
              Search
            </label>
            <input
              id="comms-search"
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Email or subject..."
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            />
          </div>

          <div>
            <label htmlFor="comms-status" className="block text-sm text-hdlcc-textMuted mb-1">
              Status
            </label>
            <select
              id="comms-status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="delivered">Delivered</option>
              <option value="pending">Pending</option>
              <option value="bounced">Bounced</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div>
            <label htmlFor="comms-template" className="block text-sm text-hdlcc-textMuted mb-1">
              Template
            </label>
            <select
              id="comms-template"
              name="template"
              value={filters.template}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            >
              <option value="">All Templates</option>
              <option value="Welcome Email">Welcome Email</option>
              <option value="Renewal Reminder">Renewal Reminder</option>
              <option value="Payment Receipt">Payment Receipt</option>
              <option value="Application Approved">Application Approved</option>
            </select>
          </div>

          <div>
            <label htmlFor="comms-date-range" className="block text-sm text-hdlcc-textMuted mb-1">
              Date Range
            </label>
            <select
              id="comms-date-range"
              name="dateRange"
              value={filters.dateRange}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>
      </div>

      {/* Email Logs */}
      <div className="bg-hdlcc-bgWhite rounded shadow-card border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-100">
                <th className="px-4 py-3 text-left text-sm font-semibold text-hdlcc-textDark">
                  Recipient
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-hdlcc-textDark">
                  Subject
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-hdlcc-textDark">
                  Template
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-hdlcc-textDark">
                  Sent
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-hdlcc-textDark">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-hdlcc-textDark">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map(log => (
                <tr key={log.id} className="border-b border-gray-200/50 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-hdlcc-textDark">{log.recipientEmail}</td>
                  <td className="px-4 py-3 text-sm text-hdlcc-textDark">{log.subject}</td>
                  <td className="px-4 py-3 text-sm text-hdlcc-textMuted">{log.templateName}</td>
                  <td className="px-4 py-3 text-sm text-hdlcc-textMuted">
                    {formatRelativeTime(log.sentAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs ${getStatusColor(log.status)}`}
                      >
                        {getStatusIcon(log.status)}
                      </span>
                      <span className="text-sm capitalize text-hdlcc-textDark">{log.status}</span>
                    </div>
                    {log.openedAt && (
                      <div className="text-xs text-hdlcc-textMuted mt-1">
                        Opened {formatRelativeTime(log.openedAt)}
                      </div>
                    )}
                    {log.errorMessage && (
                      <div className="text-xs text-hdlcc-accentRed mt-1">{log.errorMessage}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button className="text-hdlcc-accentRed hover:text-red-600 text-sm">
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredLogs.length === 0 && (
        <div className="text-center py-12 text-hdlcc-textMuted">
          <p>No email communications found.</p>
        </div>
      )}
    </div>
  );
}

export default Communications;
